// shared/domain/asistentes.js  (reglas puras sobre asistentes; `at/asistentes` devuelve una lista plana)

/**
 * Cuenta asistentes por padre. `key` es 'id_capacitacion' o 'id_visita' (ids NUMÉRICOS del padre).
 * Ignora los asistentes eliminados (deleted_at != null). Devuelve Map id -> total.
 */
export const contarAsistentes = (asistentes = [], key) => {
  const counts = new Map();
  asistentes.forEach(asistente => {
    if (asistente.deleted_at !== null && asistente.deleted_at !== undefined) return;
    const parentId = Number(asistente[key]);
    if (!parentId) return;
    counts.set(parentId, (counts.get(parentId) || 0) + 1);
  });
  return counts;
};

// Nombre del campo FK del hijo según el tipo de padre.
export const PARENT_KEYS = { capacitacion: 'id_capacitacion', visita: 'id_visita' };

/** Ids de cuenta de los asistentes vigentes (sin deleted_at), sin repetidos y numéricos. */
export const idsAsistentesActivos = (asistentes = []) => {
  const ids = asistentes
    .filter(a => a.deleted_at === null || a.deleted_at === undefined)
    .map(a => Number(a.id_cuenta))
    .filter(Boolean);
  return [...new Set(ids)];
};

/** Asistentes vigentes (sin deleted_at) con uuid, como [{ uuid, id_cuenta }] (id numérico). Es lo que se reenvía para conservarlos. */
export const asistentesVigentes = (asistentes = []) =>
  asistentes
    .filter(a => (a.deleted_at === null || a.deleted_at === undefined) && a.uuid && Number(a.id_cuenta))
    .map(a => ({ uuid: a.uuid, id_cuenta: Number(a.id_cuenta) }));

/**
 * Lista FINAL de asistentes para enviar en el mismo JSON del padre (el backend la reconcilia por uuid:
 * uuid nuevo = crea, uuid existente = conserva, ausente = soft-delete, `[]` = quita todos).
 * IMPORTANTE: a quien ya era asistente se le REENVÍA su uuid (de `existentes`, por id_cuenta); solo los agregados
 * reciben uno nuevo de `uuidFactory`. Un uuid nuevo para un asistente existente lo borraría y recrearía.
 * Sin repetidos por id_cuenta. `uuidFactory` se inyecta (application) para que el dominio siga siendo determinista.
 */
export const construirAsistentesPayload = (idsCuenta = [], existentes = [], uuidFactory) => {
  const uuidPorCuenta = new Map();
  existentes.forEach(a => {
    const id = Number(a.id_cuenta);
    if (id && a.uuid && !uuidPorCuenta.has(id)) uuidPorCuenta.set(id, a.uuid);
  });
  const vistos = new Set();
  const payload = [];
  idsCuenta.forEach(raw => {
    const id = Number(raw);
    if (!id || vistos.has(id)) return;
    vistos.add(id);
    payload.push({ uuid: uuidPorCuenta.get(id) || uuidFactory(), id_cuenta: id });
  });
  return payload;
};

/** Asistentes finales tras guardar: los de la respuesta del backend; si no vinieron, los enviados (ya eran la lista final). */
export const asistentesFinales = (guardado, enviados = []) => {
  const deRespuesta = asistentesVigentes(guardado && guardado.asistentes);
  return deRespuesta.length ? deRespuesta : enviados;
};

/** Asistente vigente más reciente (mayor id) de una lista de asistentes; null si no hay. */
export const ultimoAsistenteActivo = (asistentes = []) => {
  const vigentes = asistentes.filter(a => a.deleted_at === null || a.deleted_at === undefined);
  return vigentes.reduce((ultimo, a) => (!ultimo || Number(a.id) > Number(ultimo.id) ? a : ultimo), null);
};

/**
 * Para padres con UN solo asistente relevante (p. ej. el productor de una visita) sobre la lista plana de `at/asistentes`:
 * Map idPadre -> id_cuenta del asistente vigente más reciente. `key`: 'id_visita' | 'id_capacitacion'.
 */
export const cuentaPorPadre = (asistentes = [], key) => {
  const porPadre = new Map();
  asistentes.forEach(a => {
    const padre = Number(a[key]);
    if (padre) porPadre.set(padre, [...(porPadre.get(padre) || []), a]);
  });
  const result = new Map();
  porPadre.forEach((lista, padre) => {
    const ultimo = ultimoAsistenteActivo(lista);
    if (ultimo && Number(ultimo.id_cuenta)) result.set(padre, Number(ultimo.id_cuenta));
  });
  return result;
};
